import { useDialogs } from "@platform/hooks";
import { useCallback, useMemo } from "react";
import { useFieldMetadata } from "@platform/plan-grid-core";

import {
  createCommentContextMenuItems,
  createValidationMenuItems,
} from "../components/PlanEditor/components/ContextMenu";

import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";
import type {
  CommentDialogData,
  FieldCommentResponse,
  MenuCellTarget,
} from "@platform/plan-grid-core";

type ContextMenuItemsGetter = (
  params: GetContextMenuItemsParams,
) => (DefaultMenuItem | MenuItemDef)[];

/**
 * Encapsulates the AI Docs context menu logic.
 * Must be called inside FieldMetadataProvider + DialogProvider.
 *
 * Returns a getContextMenuItems callback suitable for PlanEditor's prop.
 */
export function useAiDocsContextMenu(
  enableContextMenu = true,
  enableCellValidation = false,
): ContextMenuItemsGetter {
  const fieldMetadata = useFieldMetadata();
  const { openDialog } = useDialogs();

  const cellContextMenuCallbacks = useMemo(
    () => ({
      getFieldComment: fieldMetadata.getFieldComment,
      onNeedsReview: (
        planId: string,
        fieldPath: string,
        existingComment?: FieldCommentResponse,
      ) => {
        const hasReview =
          existingComment?.needsReview || existingComment?.comment;
        const dialogData: CommentDialogData = {
          title: hasReview ? "Edit/View Review" : "Needs Review",
          currentComment: existingComment?.comment ?? "",
          planId,
          fieldPath,
          existingComment,
          onSubmit: (comment: string) => {
            if (existingComment) {
              // Sending a cell back to review undoes its validation: the two
              // flags name opposite states, so only one holds at a time.
              fieldMetadata.updateComment(planId, existingComment.id, {
                comment: comment || "",
                needsReview: true,
                isValidated: false,
              });
            } else {
              fieldMetadata.createComment(planId, fieldPath, comment || null);
            }
          },
        };
        openDialog<CommentDialogData>("comment", dialogData);
      },
      onClearReview: (
        planId: string,
        existingComment: FieldCommentResponse,
      ) => {
        // The record also carries flags the review doesn't own, so it can only
        // be deleted once nothing else depends on it.
        const carriesOtherFlags =
          existingComment.manuallyEdited || existingComment.isValidated;
        if (carriesOtherFlags) {
          fieldMetadata.updateComment(planId, existingComment.id, {
            comment: "",
            needsReview: false,
          });
        } else {
          fieldMetadata.removeComment(planId, existingComment.id);
        }
      },
    }),
    [openDialog, fieldMetadata],
  );

  const buildValidationItems = useCallback(
    (targets: MenuCellTarget[]) =>
      createValidationMenuItems(targets, {
        isValidated: (planId, fieldPath) =>
          Boolean(
            fieldMetadata.getFieldComment(planId, fieldPath)?.isValidated,
          ),
        getComment: (planId, fieldPath) =>
          fieldMetadata.getFieldComment(planId, fieldPath)?.comment ?? "",
        onSetValidated: fieldMetadata.setValidated,
        onEditComment: (planId, fieldPath, comment) => {
          const existingComment = fieldMetadata.getFieldComment(
            planId,
            fieldPath,
          );
          if (!existingComment) return;

          const dialogData: CommentDialogData = {
            title: "Edit/Review Comment",
            currentComment: comment,
            planId,
            fieldPath,
            existingComment,
            // Only the text changes. The cell stays validated and out of review.
            onSubmit: (updated: string) =>
              fieldMetadata.updateComment(planId, existingComment.id, {
                comment: updated || "",
              }),
          };
          openDialog<CommentDialogData>("comment", dialogData);
        },
        onClearComment: (planId, fieldPath) => {
          const existingComment = fieldMetadata.getFieldComment(
            planId,
            fieldPath,
          );
          if (!existingComment) return;

          fieldMetadata.updateComment(planId, existingComment.id, {
            comment: "",
          });
        },
      }),
    [fieldMetadata, openDialog],
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) =>
      enableContextMenu
        ? createCommentContextMenuItems(
            params,
            cellContextMenuCallbacks,
            enableCellValidation ? buildValidationItems : undefined,
          )
        : [],
    [
      enableContextMenu,
      enableCellValidation,
      cellContextMenuCallbacks,
      buildValidationItems,
    ],
  );

  return getContextMenuItems;
}
